import React, { useState } from 'react';
import styled, { css } from 'styled-components';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  onIconClick?: () => void;
}

const InputContainer = styled.div<{ $fullWidth: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
`;

const InputLabel = styled.label`
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
`;

const InputWrapper = styled.div<{ $hasIcon: boolean; $iconPosition: 'left' | 'right'; $hasError: boolean; $isFocused: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${props => 
    props.$hasError ? '#FF5C5C' : 
    props.$isFocused ? 'var(--color-primary)' : 
    'rgba(255, 255, 255, 0.12)'};
  background-color: rgba(255, 255, 255, 0.04);
  transition: all 0.2s ease;

  ${props => props.$hasIcon && props.$iconPosition === 'left' && css`
    padding-left: 40px;
  `}

  ${props => props.$hasIcon && props.$iconPosition === 'right' && css`
    padding-right: 40px;
  `}

  &:hover:not(:disabled) {
    border-color: ${props => props.$hasError ? '#FF5C5C' : 'rgba(255, 255, 255, 0.24)'};
  }
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: var(--color-text);
  font-family: var(--font-family-main);
  font-size: 16px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }
`;

const IconContainer = styled.div<{ $position: 'left' | 'right'; $isClickable: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$position === 'left' ? 'left: 12px;' : 'right: 12px;'}
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
`;

const ErrorMessage = styled.div`
  font-size: 12px;
  color: #FF5C5C;
  margin-top: 4px;
`;

export const Input: React.FC<InputProps> = ({
  label,
  error,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  onIconClick,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (rest.onFocus) rest.onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (rest.onBlur) rest.onBlur(e);
  };

  return (
    <InputContainer $fullWidth={fullWidth}>
      {label && <InputLabel>{label}</InputLabel>}
      <InputWrapper
        $hasIcon={!!icon}
        $iconPosition={iconPosition}
        $hasError={!!error}
        $isFocused={isFocused}
      >
        {icon && (
          <IconContainer 
            $position={iconPosition}
            $isClickable={!!onIconClick}
            onClick={onIconClick}
          >
            {icon}
          </IconContainer>
        )}
        <StyledInput 
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...rest}
        />
      </InputWrapper>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </InputContainer>
  );
};

export default Input; 